import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { PropelUserModel } from '../../api';
import { DATE_FORMAT } from '../consts';
import { useEditOrderDrawer } from './EditOrder/EdItOrderDrawerState';
import { useDeleteDialog } from './DeleteDialog/DeleteDialogState';
import { useShiftDialog } from './ShiftDialog/ShiftDialogState';
import { userColorPalette } from '../userColorPalette';
import { CalendarContextType } from './CalendarContextType';
import { initialCalendarContextState } from './initialCalendarContextState';
import { useExperienceAvailabilityDrawer } from './ExperienceAvailabilityDrawer/ExperienceAvailabilityDrawer';
import { CalendarView } from '../calendarWrapper/CalendarWrapper';
import TTLLocalStorage, { WEBOOK_SAVED_CURRENT_DATE } from '../../utils/TTLLocalStorage';
import { calculateFetchGap } from './calculateFetchGap';
import { getInitialCurrentDate } from './getInitialCurrentDate';
import { useSlotActionsMenu } from './SlotActionsMenu/SlotActionsMenuState';

const CalendarContext = createContext<CalendarContextType>(initialCalendarContextState);

export const useCalendar = () => useContext(CalendarContext);

interface CalendarProviderProps {
  children: ReactNode;
}

export const CalendarProvider: React.FC<CalendarProviderProps> = ({ children }) => {
  const [users, setUsers] = useState<PropelUserModel[]>([]);
  const [userColors, setUserColors] = useState<Record<string, string>>({});
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([]);
  const urlParams = new URLSearchParams(window.location.search);
  const view = urlParams.get('view');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [calendarView, setCalendarView] = useState<CalendarView>(
    view
      ? (view as CalendarView)
      : localStorage.getItem('webook-calendarview')
      ? (localStorage.getItem('webook-calendarview') as CalendarView)
      : isSmallScreen
      ? CalendarView.THREE_DAY
      : CalendarView.WEEK,
  );
  const { startDate: startDateParam } = useParams();
  const [currentDate, setCurrentDate] = useState(
    getInitialCurrentDate({
      calendarView,
      savedCurrentDate: startDateParam || TTLLocalStorage.getItem(WEBOOK_SAVED_CURRENT_DATE),
    }),
  );

  const [initialStartDay, initialEndDay] = calculateFetchGap(currentDate, calendarView);

  const [startDate, setStartDate] = useState<string>(initialStartDay.format(DATE_FORMAT));
  const [endDate, setEndDate] = useState<string>(initialEndDay.format(DATE_FORMAT));
  const [shouldBlockSwiping, setShouldBlockSwiping] = useState<boolean>(false);

  useEffect(() => {
    if (startDateParam) {
      setCurrentDate(
        getInitialCurrentDate({
          calendarView,
          savedCurrentDate: startDateParam,
        }),
      );
    }
  }, [startDateParam, calendarView]);
  const setUsersInternal = (users: PropelUserModel[]) => {
    setUsers(users);
    const userColorMap = {};
    users.forEach((user, index) => {
      // @ts-ignore
      userColorMap[user.user_id] = userColorPalette[index % userColorPalette.length];
    });
    setUserColors(userColorMap);
  };
  const {
    open: openEditOrderDrawer,
    close: closeEditOrderDrawer,
    drawerState: editOrderDrawerState,
  } = useEditOrderDrawer();

  const {
    open: openDeleteOrderDialog,
    close: closeDeleteOrderDialog,
    drawerState: deleteOrderDialogState,
  } = useDeleteDialog();

  const {
    open: openDeleteEventDialog,
    close: closeDeleteEventDialog,
    drawerState: deleteEventDialogState,
  } = useDeleteDialog();

  const {
    open: openDeleteUnavailabilityDialog,
    close: closeDeleteUnavailabilityDialog,
    drawerState: deleteUnavailabilityDialogState,
  } = useDeleteDialog();

  const {
    open: openDeleteShiftDialog,
    close: closeDeleteShiftDialog,
    drawerState: deleteShiftDialogState,
  } = useDeleteDialog();

  const {
    open: openEditShiftDialog,
    close: closeEditShiftDialog,
    drawerState: editShiftDialogState,
  } = useShiftDialog();

  const {
    open: openExperienceAvailabilityDrawer,
    close: closeExperienceAvailabilityDrawer,
    drawerState: experienceAvailabilityDrawerState,
  } = useExperienceAvailabilityDrawer();

  const {
    open: openSlotActionsMenu,
    close: closeSlotActionsMenu,
    drawerState: slotActionsMenuState,
  } = useSlotActionsMenu();
  return (
    <CalendarContext.Provider
      value={{
        openExperienceAvailabilityDrawer,
        closeExperienceAvailabilityDrawer,
        experienceAvailabilityDrawerState,

        openEditOrderDrawer,
        closeEditOrderDrawer,
        editOrderDrawerState,

        openDeleteOrderDialog,
        closeDeleteOrderDialog,
        deleteOrderDialogState,

        openDeleteEventDialog,
        closeDeleteEventDialog,
        deleteEventDialogState,

        openDeleteUnavailabilityDialog,
        closeDeleteUnavailabilityDialog,
        deleteUnavailabilityDialogState,

        openDeleteShiftDialog,
        closeDeleteShiftDialog,
        deleteShiftDialogState,

        openEditShiftDialog,
        closeEditShiftDialog,
        editShiftDialogState,

        openSlotActionsMenu,
        closeSlotActionsMenu,
        slotActionsMenuState,

        users,
        setUsers: setUsersInternal,

        startDate,
        endDate,
        setStartDate,
        setEndDate,

        userColors,
        selectedUsersIds,
        setSelectedUsersIds,
        shouldBlockSwiping,
        setShouldBlockSwiping,

        calendarView,
        setCalendarView,

        currentDate,
        setCurrentDate,

        isAnyThingOpen:
          editOrderDrawerState.isOpen ||
          deleteOrderDialogState.isOpen ||
          deleteEventDialogState.isOpen ||
          deleteUnavailabilityDialogState.isOpen ||
          deleteShiftDialogState.isOpen ||
          editShiftDialogState.isOpen ||
          experienceAvailabilityDrawerState.isOpen ||
          slotActionsMenuState.isOpen,
      }}>
      {children}
    </CalendarContext.Provider>
  );
};
